import React, { useEffect, useMemo, useState } from "react";
import { Button, FileInput, Modal } from "flowbite-react";
import { toast } from "react-toastify";
import { UserImportApi } from "../../Api/UserImportApi"; // uses uploadFemaCsv(file)

type ImportResultRow = {
  rowNumber: number;
  email?: string;
  username?: string;
  password?: string;
  status: "Updated" | "Created" | "Skipped" | "Error" | string;
  message?: string;
};

type ImportSummary = {
  total: number;
  updated: number;
  created: number;
  skipped: number;
  errors: number;
  rows: ImportResultRow[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  onDone?: () => void; // e.g., refresh your table after success
};

const statusColor: Record<string, string> = {
  Updated: "text-green-700 bg-green-100",
  Created: "text-blue-700 bg-blue-100",
  Skipped: "text-gray-700 bg-gray-100",
  Error: "text-red-700 bg-red-100",
};

export default function FemaCsvImportModal({ open, onClose, onDone }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Reset state whenever the modal opens
  useEffect(() => {
    if (open) {
      setFile(null);
      setLoading(false);
      setResult(null);
      setShowDetails(false);
    }
  }, [open]);

  const countsText = useMemo(() => {
    if (!result) return "";
    const { total, updated, created, skipped, errors } = result;
    return `Total: ${total}  •  Updated: ${updated}  •  Created: ${created}  •  Skipped: ${skipped}  •  Errors: ${errors}`;
    // (Backend sample earlier didn't create users; keep Created=0 unless you add that logic)
  }, [result]);

  const handleUpload = async () => {
    if (!file) {
      toast.error("Choose a CSV file first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await UserImportApi.uploadFemaCsv(file);
      setResult(data as ImportSummary);
      toast.success("Import completed.");
      onDone?.(); // refresh users list
    } catch (e: any) {
      const msg = e?.response?.data ?? "Import failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={open} onClose={onClose} size="xl">
      <Modal.Header>Import FEMA Credentials (CSV)</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Upload a CSV containing <b>Email</b>, <b>IndividualId/Username</b>,
            and <b>Password</b>. We’ll match users by{" "}
            <b>email (case-insensitive)</b> and update only the provided fields.
          </div>

          <div className="flex items-center gap-3">
            <FileInput
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button
              onClick={handleUpload}
              isProcessing={loading}
              disabled={!file}
              color="blue"
            >
              Upload
            </Button>
          </div>

          {result && (
            <div className="space-y-3">
              <div className="rounded border p-3 text-sm">
                <div className="font-medium mb-1">Summary</div>
                <div className="text-gray-800">{countsText}</div>
                <div className="mt-2">
                  <button
                    className="text-blue-600 hover:underline text-sm"
                    onClick={() => setShowDetails((v) => !v)}
                  >
                    {showDetails ? "Hide details" : "Show details"}
                  </button>
                </div>
              </div>

              {showDetails && (
                <div className="max-h-80 overflow-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Row</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Username</th>
                        <th className="px-3 py-2 text-left">Password</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{r.rowNumber}</td>
                          <td className="px-3 py-2">{r.email}</td>
                          <td className="px-3 py-2">{r.username}</td>
                          <td className="px-3 py-2">{r.password}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`px-2 py-0.5 rounded text-xs ${
                                statusColor[r.status] ||
                                "text-gray-700 bg-gray-100"
                              }`}
                            >
                              {r.status}
                            </span>
                          </td>
                          <td className="px-3 py-2">{r.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="light" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
